import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CreditCard, Users, Briefcase, Check, AlertTriangle } from "lucide-react";

const STATUS = {
  trial: { label: "Periodo di prova", cls: "bg-blue-100 text-blue-800 hover:bg-blue-100" },
  active: { label: "Attivo", cls: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" },
  suspended: { label: "Sospeso", cls: "bg-gray-200 text-gray-700 hover:bg-gray-200" },
  expired: { label: "Scaduto", cls: "bg-red-100 text-red-800 hover:bg-red-100" },
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return iso; }
};

function UsageBar({ label, icon: Icon, used, limit, testid }) {
  const unlimited = limit == null;
  const pct = unlimited ? 0 : Math.min(100, (used / limit) * 100);
  const full = !unlimited && used >= limit;
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-sm mb-1.5">
        <span className="flex items-center gap-1.5 text-muted-foreground"><Icon className="w-4 h-4" /> {label}</span>
        <span className={`font-mono ${full ? "text-red-600 font-semibold" : ""}`}>{used} / {unlimited ? "∞" : limit}</span>
      </div>
      {!unlimited && (
        <div className="h-2 bg-secondary">
          <div className={`h-2 transition-all ${full ? "bg-red-500" : "bg-primary"}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {full && (
        <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Limite raggiunto — aggiorna il piano</p>
      )}
    </div>
  );
}

export default function SubscriptionPage() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payCfg, setPayCfg] = useState(null);
  const [txs, setTxs] = useState([]);
  const [paying, setPaying] = useState(false);

  const loadAll = () => {
    api.get("/subscription").then(({ data }) => setSub(data)).catch(() => setSub(null)).finally(() => setLoading(false));
    api.get("/payments/transactions").then(({ data }) => setTxs(data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/payments/config").then(({ data }) => setPayCfg(data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (params.get("paypal") === "success" && token) {
      window.history.replaceState({}, "", "/abbonamento");
      setPaying(true);
      api.post("/payments/subscription/capture", { order_id: token })
        .then(() => toast.success("Pagamento ricevuto: abbonamento rinnovato di 1 mese"))
        .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Errore nella conferma del pagamento"))
        .finally(() => { setPaying(false); loadAll(); });
    } else {
      if (params.get("paypal") === "cancel") {
        window.history.replaceState({}, "", "/abbonamento");
        toast.info("Pagamento annullato");
      }
      loadAll();
    }
  }, []);

  const payWithPaypal = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/subscription/checkout");
      window.location.href = data.approve_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore PayPal");
      setPaying(false);
    }
  };

  if (loading) {
    return <Layout><div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div></Layout>;
  }

  const status = sub?.status ? STATUS[sub.status] : null;

  return (
    <Layout>
      <PageHeader title="Abbonamento" subtitle="Piano dello studio e utilizzo" />
      <div className="p-8 space-y-6" data-testid="subscription-page">
        {!sub?.has_plan ? (
          <div className="border border-dashed border-border bg-white p-12 text-center max-w-2xl" data-testid="no-plan-box">
            <CreditCard className="w-10 h-10 mx-auto text-muted-foreground/50" />
            <p className="mt-3 text-muted-foreground">Nessun piano assegnato a questo studio.<br />Contatta il gestore della piattaforma per attivare un abbonamento.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white border border-border p-6" data-testid="current-plan-card">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="data-label">Piano attuale</div>
                  <div className="font-heading font-black text-3xl tracking-tighter mt-1" data-testid="current-plan-name">{sub.plan.name}</div>
                </div>
                {status && <Badge className={`rounded-none ${status.cls}`} data-testid="subscription-status-badge">{status.label}</Badge>}
              </div>
              <div className="mt-4 font-heading font-black text-4xl tracking-tighter">
                € {sub.plan.price_monthly}<span className="text-base font-medium text-muted-foreground"> /mese</span>
              </div>
              <div className="mt-4 text-sm text-muted-foreground space-y-1">
                {sub.status === "trial" && sub.trial_ends_at && (
                  <p data-testid="trial-ends">Prova gratuita fino al <span className="font-medium text-foreground">{fmtDate(sub.trial_ends_at)}</span></p>
                )}
                {sub.expires_at && (
                  <p data-testid="expires-at">Rinnovo / scadenza: <span className="font-medium text-foreground">{fmtDate(sub.expires_at)}</span></p>
                )}
                {sub.status === "expired" && (
                  <p className="text-red-600 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Abbonamento scaduto: la creazione di nuovi utenti e clienti è bloccata.</p>
                )}
              </div>
              <div className="mt-5 pt-4 border-t border-border">
                <Button data-testid="paypal-renew-btn" onClick={payWithPaypal} disabled={paying || !payCfg?.configured} className="rounded-none bg-[#0070BA] hover:bg-[#005ea6] text-white">
                  {paying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                  Rinnova ora con PayPal — € {sub.plan.price_monthly} (1 mese)
                </Button>
                {payCfg && !payCfg.configured && (
                  <p className="text-xs text-muted-foreground mt-2" data-testid="paypal-not-configured-note">
                    Pagamenti PayPal non ancora attivi: il gestore deve configurare le credenziali PayPal.
                  </p>
                )}
                {payCfg?.configured && payCfg.mode === "sandbox" && (
                  <p className="text-xs text-muted-foreground mt-2">Modalità test (sandbox): nessun addebito reale.</p>
                )}
              </div>
            </div>
            <div className="bg-white border border-border p-6 space-y-5" data-testid="usage-card">
              <div className="data-label">Utilizzo</div>
              <UsageBar label="Utenti" icon={Users} used={sub.usage.users} limit={sub.plan.max_users} testid="usage-users" />
              <UsageBar label="Clienti" icon={Briefcase} used={sub.usage.clients} limit={sub.plan.max_clients} testid="usage-clients" />
            </div>
          </div>
        )}

        {txs.length > 0 && (
          <div className="bg-white border border-border p-6 max-w-3xl" data-testid="payments-history">
            <div className="data-label mb-3">Ultimi pagamenti</div>
            <div className="space-y-2 text-sm">
              {txs.slice(0, 8).map((t) => (
                <div key={t.id} className="flex items-center justify-between border-b border-border/60 pb-2 last:border-0">
                  <span className="text-muted-foreground">{fmtDate(t.created_at)} · {t.kind === "subscription" ? `Rinnovo piano ${t.plan_id}` : "Acconto proposta"}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-mono">€ {t.amount}</span>
                    <Badge className={`rounded-none ${t.status === "completed" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-gray-200 text-gray-700 hover:bg-gray-200"}`}>
                      {t.status === "completed" ? "Pagato" : "In sospeso"}
                    </Badge>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {sub?.plans?.length > 0 && (
          <div>
            <h2 className="font-heading font-black text-lg tracking-tight mb-3">Piani disponibili</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="plans-comparison">
              {sub.plans.map((p) => {
                const current = sub.has_plan && sub.plan?.id === p.id;
                return (
                  <div key={p.id} className={`bg-white border p-6 ${current ? "border-primary border-2" : "border-border"}`} data-testid={`compare-plan-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-heading font-black text-xl tracking-tight">{p.name}</div>
                      {current && <Badge className="rounded-none">Piano attuale</Badge>}
                    </div>
                    <div className="mt-2 font-heading font-black text-3xl tracking-tighter">
                      € {p.price_monthly}<span className="text-sm font-medium text-muted-foreground"> /mese</span>
                    </div>
                    <ul className="mt-4 space-y-1.5 text-sm">
                      {(p.features || []).map((f, i) => (
                        <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {f}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            <p className="text-sm text-muted-foreground mt-4" data-testid="upgrade-note">
              Per cambiare piano o rinnovare l'abbonamento contatta il gestore della piattaforma.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
